import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges, SimpleChanges
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, map, tap, zip } from 'rxjs';
import isEqual from 'lodash-es/isEqual';

import { TravelerInfoType, TravelerTypes } from '@pax/entities/traveler-info';
import { User } from '@app/modules/core/interfaces/user';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { TravelerPaxResponse, TravelerPax, TravelerPaxResponseVm, TravelerForm } from '@pax/entities/traveler-pax';
import { CountriesVm } from '@booking/entities/countries';
import { PaxFacade } from '@pax/pax.facade';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { BusinessRulesByBookingType, BookingPaxTypeDocumentProps } from '@app/entities/booking-documents';
import { PaxDocumentsPipe } from '@pax/pipes/pax-documents.pipe';
import { BOOKING_FORM_INPUTS, TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { Travelers } from '@booking/entities/booking-arrangement';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { isNullOrUndefined } from '@app/util/util';
import { PaxFormService } from '@pax/services/pax-form.service';
import { formatStrinWithTimeZoneDateByRegex } from '@app/modules/ux/mappers/date.mapper';
import { isEnabledPaxDocumentModule } from '../../mappers/pax-traveler-documents.mapper';
import { internationalPaxDocumentFilledOut, travelerFormDocumentType } from '@forms/mappers/form-document-input.mapper';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';
import { getBookingInfoTypeByTravelerInfoType } from '@pax/mappers/get-pax-info-by-types.mapper';
import { isEqualsUserData } from '@pax/mappers/default-pax.mapper';
import { FormValidationService } from '@reservation/services/form-validation.service';

@Component({
  selector: 'app-traveler-information',
  templateUrl: './traveler-information.container.html',
  styleUrls: ['./traveler-information.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TravelerInformationContainer implements OnChanges {

  @Input() form: UntypedFormGroup;
  @Input() user: User;
  @Input() travelerInfoType: TravelerInfoType = TravelerInfoType.PASSENGER;
  @Input() travelerType: TravelerTypes;
  @Input() travelerBookingProvider: TravelBookingType = TravelBookingType.FLIGHTS;
  @Input() paxTraveler: TravelerPaxResponse;
  @Input() travelers: TravelerPaxResponse[] = [];
  @Input() countries: CountriesVm[] = [];
  @Input() frequentFlyerPrograms: FrequentFlyerResponse[] = [];
  @Input() showFrequentFlyer: boolean;
  @Input() isSubmitted: boolean = false;
  @Input() isSubmittedSuccess: boolean = false;
  @Input() isAnInternationalTrip: boolean;
  @Input() travelerNumber: number;
  @Input() ordinalNumber: number;
  @Input() travelerAge: number | null = null;
  @Input() businessRules: BusinessRulesByBookingType;
  @Input() productCode: string;
  @Input() productProvider: string;
  @Input() bookingSource: HotelProvider;
  @Input() isLoadingPaxForm: boolean;
  @Input() showForm: boolean;
  @Input() isMobile: boolean;
  @Input() validator: AirValidationResponse | VehicleValidateResponse | HotelValidationResponse;
  @Input() paxTravelerHasBeenSelected: boolean;

  @Output() update: EventEmitter<TravelerPaxResponseVm> = new EventEmitter();
  @Output() create: EventEmitter<TravelerPax> = new EventEmitter();
  @Output() closeForm: EventEmitter<void> = new EventEmitter();
  @Output() selected: EventEmitter<void> = new EventEmitter();

  public paxForm: UntypedFormGroup;

  constructor(
    private readonly facade: PaxFacade,
    private readonly paxDocumentsPipe: PaxDocumentsPipe,
    private readonly paxFormService: PaxFormService,
    private readonly formValidationService: FormValidationService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.paxTraveler) {
      this.isSubmitted = false;
      this.createPaxForm();
      if (isNullOrUndefined(this.addDynamicElement.at(this.travelerNumber))) {
        this.addDynamicElement.push(this.paxForm);
      }
      if (!this.disabledDocumentInputs) {
        this.setPaxDocumentSchemaByDefaultData();
      }
    }
    if (
      (
        changes.paxTravelerHasBeenSelected?.currentValue ||
        !isEqual(
          this.paxForm?.value?.FrequentFlyer,
          this.addDynamicElement?.at(this.travelerNumber)?.value?.FrequentFlyer
        ) ||
        !isEqual(
          this.paxForm?.value?.FrequentFlyerAirline,
          this.addDynamicElement?.at(this.travelerNumber)?.value?.FrequentFlyerAirline
        )
      ) &&
      !isNullOrUndefined(this.paxForm)
    ) {
      if (!isNullOrUndefined(this.addDynamicElement.at(this.travelerNumber))) {
        this.addDynamicElement.removeAt(this.travelerNumber);
        this.addDynamicElement.insert(this.travelerNumber, this.paxForm);
        this.selected.emit();
      }
    }
  }

  get documents$(): Observable<BookingPaxTypeDocumentProps[]> {
    return this.paxDocumentsPipe.transform(this.travelerType, this.travelerValue?.Nationality);
  }

  get isCreateTraveler$(): Observable<boolean> {
    return this.facade.isCreateTraveler$;
  }

  get travelerLabel(): string {
    return getBookingInfoTypeByTravelerInfoType(this.travelerInfoType);
  }

  get travelerInfoTypes(): typeof TravelerInfoType {
    return TravelerInfoType;
  }

  private get travelerValue(): Travelers {
    return this.addDynamicElement?.at(this.travelerNumber)?.value as Travelers;
  }

  private get addDynamicElement(): UntypedFormArray {
    return this.form?.get(BOOKING_FORM_INPUTS.TRAVELERS) as UntypedFormArray;
  }

  get isNotRequiredHotelFormGuest(): boolean {
    return this.travelerBookingProvider === TravelBookingType.HOTELS && this.ordinalNumber > 1;
  }

  public setCreatePaxUI(payload: boolean): void {
    this.facade.setCreatePAXUI(payload);
  }

  get hasPassportRestriction(): boolean {
    return this.paxFormService.hasPassportRestriction(this.isAnInternationalTrip, this.businessRules);
  }

  get defaultFormData$(): Observable<TravelerForm> {
    return this.documents$.pipe(
      map(documents => this.paxFormService.defaultFormData(
        this.paxTraveler, 
        this.user, 
        this.travelerType,
        this.travelerNumber, 
        this.countries, 
        documents,
        this.travelers?.findIndex(t => isEqualsUserData(this.user, t),
        this.validator as AirValidationResponse,
        ))
      )
    );
  }

  get disabledDocumentInputs(): boolean {
    return !isEnabledPaxDocumentModule(this.businessRules, this.isAnInternationalTrip, this.productCode, this.productProvider);
  }

  get departureDateReservation(): Date {
    const dateString = (this.validator as AirValidationResponse)?.TripOption?.Sectors[0]?.Flights[0]?.DepartureDate;
    return dateString ? new Date(formatStrinWithTimeZoneDateByRegex(dateString)) : new Date();
  }

  get pickUpTime(): Date {
    return new Date(formatStrinWithTimeZoneDateByRegex(
      (this.validator as VehicleValidateResponse)?.VehicleOption?.PickUp?.Time)
    );
  }

  get checkInDate(): Date {
    return (this.validator as HotelValidationResponse)?.HotelReservation ?
      new Date(formatStrinWithTimeZoneDateByRegex(
        (this.validator as HotelValidationResponse).HotelReservation.Checkin)) : new Date();
  }

  get documentType$(): Observable<BookingDocumentPaxIdType> {
    return this.documents$.pipe(
      map(documents => travelerFormDocumentType(this.paxForm, documents))
    )
  }

  public submitForm(createForm: TravelerPax, updateForm: TravelerPaxResponseVm): void {
    if (!isNullOrUndefined(this.addDynamicElement.at(this.travelerNumber))) {
      this.addDynamicElement.removeAt(this.travelerNumber);
      this.addDynamicElement.insert(this.travelerNumber, this.paxForm);
    }
    if (!isNullOrUndefined(updateForm)) {
      this.update.emit(updateForm);
    } else if (!isNullOrUndefined(createForm)) {
      this.create.emit(createForm);
    }
  }

  private internationalDocumentFilledOut(documentType: BookingDocumentPaxIdType): boolean {
    return internationalPaxDocumentFilledOut(documentType, this.isAnInternationalTrip);
  }

  private formControls(
    defaultFormData: TravelerForm,
    documents: BookingPaxTypeDocumentProps[]): { [key: string]: UntypedFormControl } {
    const date = this.travelerInfoType === TravelerInfoType.GUEST ? this.checkInDate :
      this.travelerInfoType === TravelerInfoType.DRIVER ? new Date(this.pickUpTime) :
        this.departureDateReservation
    return this.paxFormService.formControls(
      defaultFormData, date,
      this.travelerType,
      this.travelerInfoType,
      this.travelerBookingProvider,
      this.disabledDocumentInputs,
      this.bookingSource,
      documents,
      this.travelerAge
    );
  }

  private createPaxForm(): void {
    zip(
      this.defaultFormData$,
      this.documents$,
      this.documentType$
    ).pipe(
      map(([defaultFormData, documents, documentType]) => {
        this.paxForm = new UntypedFormGroup(this.formControls(defaultFormData, documents));
        if (this.hasPassportRestriction) {
          this.paxFormService.addDocumentRestriction(
            this.paxForm,
            this.internationalDocumentFilledOut(documentType)
          );
        }
        if (this.isNotRequiredHotelFormGuest) {
          this.removeValidators();
        }
      }
      )).subscribe().unsubscribe();
  }

  private removeValidators(): void {
    this.paxForm?.get(TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER)?.setValidators(null);
    this.paxForm?.get(TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER)?.updateValueAndValidity();
    this.paxForm?.get(TRAVELER_FORM_INPUTS.NATIONALITY)?.setValidators(null);
    this.paxForm?.get(TRAVELER_FORM_INPUTS.NATIONALITY)?.updateValueAndValidity();
    this.paxForm?.get(TRAVELER_FORM_INPUTS.DOCUMENT_ID)?.setValidators(null);
    this.paxForm?.get(TRAVELER_FORM_INPUTS.DOCUMENT_ID)?.updateValueAndValidity();
  }

  private setPaxDocumentSchemaByDefaultData(): void {
    zip(this.documents$, this.documentType$).pipe(
      tap(([documents, documentType]) => {
        this.formValidationService.updatePaxDocumentSchema(this.paxForm, documents, documentType);
      })
    ).subscribe().unsubscribe();
  }

}
